import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_LABELS, formatCents, type Account, type AccountKind, type Contact, type OpenBalance, type PurchaseOpenBalance } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { CardLayout } from '../../components/CardLayout';
import { ContactFinanceEditor, ContactFinanceFactBox, CreditorFactBox } from './ContactFinance';
import { accountPath, AccountStateBadge } from './AccountsPage';
import '../offers/offers.css';
import './invoices.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });

/**
 * Maske eines Debitors oder Kreditors (KON-161): Nummer und Stand, Kontaktdaten, offene Posten, Notiz und die Finanzdaten (Zahlungsbedingungen, USt-IdNr., Bank). Das
 * Konto lässt sich sperren, aber nicht löschen, Nummer und Kontakt ändern sich nie. Verknüpft mit dem Kontakt und, wenn es ihn gibt, mit dem Konto der anderen Art.
 * Daneben die Infobox: beim Debitor Umsatz und Zahlungsverhalten des Kunden, beim Kreditor Einkauf, Verbindlichkeiten und das eigene Zahlverhalten.
 */
export function AccountDetailPage({ kind }: { kind: AccountKind }) {
  const { id = '' } = useParams();
  const label = ACCOUNT_LABELS[kind];
  const base = accountPath(kind);
  const endpoint = kind === 'debtor' ? '/debtors' : '/creditors';
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // Hooks stehen vor den frühen Rückgaben, sonst bricht die Maske beim Laden ab
  const editorRef = useRef<HTMLDivElement>(null);

  const account = useQuery({ queryKey: ['account', kind, id], queryFn: async () => (await api<{ data: Account }>('GET', `${endpoint}/${id}`)).data });
  const a = account.data;
  const contact = useQuery({ queryKey: ['contact', a?.contactId], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${a!.contactId}`)).data, enabled: !!a });
  const balance = useQuery({
    queryKey: ['account-balance', kind, a?.contactId],
    queryFn: async () =>
      kind === 'debtor'
        ? (await api<{ data: OpenBalance }>('GET', `/invoices-out/open-balance?contactId=${a!.contactId}`)).data
        : (await api<{ data: PurchaseOpenBalance }>('GET', `/invoices-in/open-balance?supplierId=${a!.contactId}`)).data,
    enabled: !!a && (kind === 'debtor' ? hasModule('invoices_out') && can('invoices_out.read') && can('invoices_out.read_amounts') : hasModule('invoices_in') && can('invoices_in.read')),
  });
  useEffect(() => {
    if (a) setNote(a.note ?? '');
  }, [a?.id, a?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useMutation({
    mutationFn: async (body: { active?: boolean; note?: string | null }) => (await api<{ data: Account }>('PATCH', `${endpoint}/${id}`, body)).data,
    onSuccess: (data) => {
      client.setQueryData(['account', kind, id], data);
      client.invalidateQueries({ queryKey: ['accounts', kind] });
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Das hat nicht geklappt.'),
  });

  if (account.isLoading) return <p className="muted">Wird geladen …</p>;
  if (account.isError || !a) {
    return (
      <div className="card narrow">
        <h1>{label.singular} nicht gefunden</h1>
        <Link to={base}>Zur Liste</Link>
      </div>
    );
  }
  const other = ACCOUNT_LABELS[kind === 'debtor' ? 'creditor' : 'debtor'];
  const otherPath = accountPath(kind === 'debtor' ? 'creditor' : 'debtor');
  const mayEdit = can('contacts.finance');
  const toEditor = () => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const invoicesLink = kind === 'debtor' ? `/rechnungen?q=${encodeURIComponent(a.name)}` : `/eingangsrechnungen?q=${encodeURIComponent(a.name)}`;
  const address = [a.street, [a.postalCode, a.city].filter(Boolean).join(' ')].filter(Boolean);
  const factbox = contact.data ? (kind === 'debtor' ? <ContactFinanceFactBox contact={contact.data} onEdit={toEditor} /> : <CreditorFactBox contact={contact.data} onEdit={toEditor} />) : null;

  const body = (
    <>
      <section className="card">
        <div className="offer-head">
          <div>
            <div className="muted small">{label.person}</div>
            <Link to={`/kontakte/${a.contactId}`} className="strong-link">{a.name}</Link>
            {address.map((line) => <div key={line}>{line}</div>)}
            {a.email && <div className="muted small">{a.email}</div>}
            {a.phone && <div className="muted small">{a.phone}</div>}
          </div>
          <dl className="offer-meta">
            <dt>{label.number}</dt>
            <dd><strong>{a.number}</strong></dd>
            <dt>Angelegt</dt>
            <dd>{stamp.format(new Date(a.createdAt))}{a.createdByName ? ` von ${a.createdByName}` : ''}</dd>
            {a.otherAccountId && (<><dt>Auch</dt><dd><Link to={`${otherPath}/${a.otherAccountId}`}>{other.singular}</Link></dd></>)}
            {balance.data && (
              <>
                <dt>{kind === 'debtor' ? 'Offene Posten' : 'Offene Verbindlichkeiten'}</dt>
                <dd>{balance.data.count === 0 ? 'keine' : <Link to={`${invoicesLink}&payment=open`}>{formatCents(balance.data.openCents)} ({balance.data.count})</Link>}</dd>
                {balance.data.overdueCount > 0 && (<><dt>Davon überfällig</dt><dd><span className="finance-balance__late">{formatCents(balance.data.overdueCents)} ({balance.data.overdueCount})</span></dd></>)}
              </>
            )}
          </dl>
        </div>
        <div className="invoice-actions">
          <Link className="btn btn--secondary btn--sm" to={`/kontakte/${a.contactId}`}>Kontakt öffnen</Link>
          <Link className="btn btn--secondary btn--sm" to={invoicesLink}>{kind === 'debtor' ? 'Rechnungen ansehen' : 'Eingangsrechnungen ansehen'}</Link>
          {!a.otherAccountId && mayEdit && hasModule(kind === 'debtor' ? 'invoices_in' : 'invoices_out') && (
            <Link className="btn btn--secondary btn--sm" to={`${otherPath}/neu?kontakt=${a.contactId}`}>Auch als {other.singular} anlegen</Link>
          )}
          {mayEdit && (
            <button type="button" className={`btn btn--sm ${a.active ? 'btn--danger' : 'btn--secondary'}`} disabled={update.isPending} onClick={() => (a.active ? window.confirm(`${label.singular} ${a.number} sperren? Das Konto und seine Belege bleiben erhalten.`) && update.mutate({ active: false }) : update.mutate({ active: true }))}>
              {a.active ? 'Sperren' : 'Entsperren'}
            </button>
          )}
        </div>
      </section>

      <section className="card">
        <h2>Notiz</h2>
        <label className="field">
          <span className="sr-only">Notiz zum Konto</span>
          <textarea className="input textarea textarea--short" value={note} onChange={(e) => setNote(e.target.value)} disabled={!mayEdit} maxLength={2000} placeholder="Interne Notiz, zum Beispiel Ansprechpartner in der Buchhaltung" />
        </label>
        {mayEdit && (
          <div className="form-actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={update.isPending || note.trim() === (a.note ?? '')} onClick={() => update.mutate({ note: note.trim() || null })}>Notiz speichern</button>
          </div>
        )}
      </section>

      {contact.data && <div ref={editorRef}><ContactFinanceEditor contact={contact.data} scope={kind} /></div>}
    </>
  );

  return (
    <>
      <p><Link to={base} className="back-link">← Alle {label.plural}</Link></p>
      <div className="page-head">
        <h1>{label.singular} {a.number}</h1>
        <AccountStateBadge active={a.active} />
        <span className="muted">{a.name}</span>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {!a.active && <div className="alert">Dieses Konto ist gesperrt. Es bleibt mit allen Belegen erhalten, taucht aber nur noch unter „Gesperrte“ auf.</div>}

      {/* Solange der Kontakt lädt, steht die Karte allein, danach mit Infobox */}
      {factbox ? <CardLayout className="cardlayout--account" factbox={factbox}>{body}</CardLayout> : body}
    </>
  );
}
